import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { CalendarClock } from 'lucide-react';

import { Button } from '@/components/Button';
import { Sheet } from '@/components/Sheet';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { SNAP_MINUTES } from '@/lib/motion';
import { formatDuration } from '@/features/tasks/tree';
import type { Task } from '@/features/tasks/types';
import type { WorkBlock } from '@/features/time/useWorkBlocks';

const WORK_BLOCKS_KEY = ['work-blocks'] as const;

function useUpcomingWorkBlocks(timezone: string, days = 7) {
  const dates = useMemo(() => {
    const start = DateTime.now().setZone(timezone);
    return Array.from({ length: days }, (_, i) => start.plus({ days: i }).toISODate()!);
  }, [timezone, days]);

  const queries = useQueries({
    queries: dates.map((date) => ({
      queryKey: [...WORK_BLOCKS_KEY, date],
      queryFn: () => api<WorkBlock[]>(`/api/work-blocks?date=${encodeURIComponent(date)}`),
      staleTime: 30_000,
    })),
  });

  const blocks = useMemo(() => {
    const seen = new Set<number>();
    const list: WorkBlock[] = [];
    for (const q of queries) {
      for (const block of q.data ?? []) {
        if (block.status !== 'scheduled' || seen.has(block.id)) continue;
        seen.add(block.id);
        list.push(block);
      }
    }
    return list.sort(
      (a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime(),
    );
  }, [queries]);

  return {
    blocks,
    isLoading: queries.some((q) => q.isLoading),
  };
}

interface Props {
  task: Task;
  timezone: string;
  onClose: () => void;
  onPickBlock: (block: WorkBlock) => void;
  onPickTime: () => void;
  submitting?: boolean;
}

/**
 * Schedule a task into a planned work block, or pick a custom time.
 */
export function AssignToBlockSheet({
  task,
  timezone,
  onClose,
  onPickBlock,
  onPickTime,
  submitting,
}: Props) {
  const { blocks, isLoading } = useUpcomingWorkBlocks(timezone);
  const duration = task.estimate_minutes ?? SNAP_MINUTES * 2;

  const formatBlock = (block: WorkBlock) => {
    const start = DateTime.fromISO(block.starts_at).setZone(timezone);
    const end = DateTime.fromISO(block.ends_at).setZone(timezone);
    const sameDay = start.hasSame(end, 'day');
    const day = start.toFormat('ccc, LLL d');
    const time = sameDay
      ? `${start.toFormat('h:mm a')} – ${end.toFormat('h:mm a')}`
      : `${start.toFormat('h:mm a')} – ${end.toFormat('ccc h:mm a')}`;
    return { day, time };
  };

  return (
    <Sheet
      title="Schedule"
      onClose={onClose}
      footer={
        <div className="flex justify-end">
          <Button variant="ghost" onClick={onClose} className="min-h-control px-3 text-small">
            Cancel
          </Button>
        </div>
      }
    >
      <p className="mb-3 truncate text-body text-text">{task.title}</p>
      <p className="mb-3 text-small text-text-secondary">
        {formatDuration(duration) ?? `${duration}m`} estimated: placed in the first open slot
        inside a block when there is room.
      </p>

      <div className="mb-4">
        <Button
          variant="secondary"
          onClick={onPickTime}
          disabled={submitting}
          className="min-h-control w-full justify-start gap-2 px-3 text-small"
        >
          <CalendarClock size={14} strokeWidth={1.5} aria-hidden />
          Pick a time…
        </Button>
      </div>

      <h3 className="mb-1.5 text-label uppercase text-text-tertiary">Work blocks</h3>

      {isLoading ? (
        <div className="space-y-2" aria-hidden>
          {[0, 1].map((i) => (
            <div key={i} className="h-12 animate-pulse rounded bg-surface-2" />
          ))}
        </div>
      ) : blocks.length === 0 ? (
        <p className="text-small text-text-tertiary">
          No planned blocks yet. Start one from Today, or pick a time manually.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {blocks.map((block) => {
            const { day, time } = formatBlock(block);
            const room = block.sprint_budget_minutes - block.selected_minutes;
            const fits = room >= duration;
            return (
              <li key={block.id}>
                <button
                  type="button"
                  disabled={submitting || !fits}
                  onClick={() => onPickBlock(block)}
                  className={cn(
                    'pressable w-full rounded-lg border px-3 py-2 text-left',
                    fits
                      ? 'border-border bg-surface-1 hover:bg-surface-2'
                      : 'cursor-not-allowed border-border/60 bg-surface-1/50 opacity-60',
                  )}
                >
                  <span className="block text-small font-medium text-text">{day}</span>
                  <span className="mt-0.5 block tabular text-small text-text-secondary">
                    {time}
                  </span>
                  <span className="mt-0.5 block text-label text-text-tertiary">
                    {fits
                      ? `${room}m open in block`
                      : `Only ${Math.max(0, room)}m free: not enough for this estimate`}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Sheet>
  );
}
